import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import SiteFooter from '@/components/SiteFooter';

const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', company: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = 'Please enter your name.';
    else if (form.name.trim().length > 100) err.name = 'Name is too long.';
    if (!form.email.trim()) err.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) err.email = 'Please enter a valid email address.';
    if (!form.subject.trim()) err.subject = 'Please enter a subject.';
    else if (form.subject.trim().length > 150) err.subject = 'Subject is too long.';
    if (!form.message.trim()) err.message = 'Please enter a message.';
    else if (form.message.trim().length < 10) err.message = 'Message should be at least 10 characters.';
    else if (form.message.trim().length > 5000) err.message = 'Message is too long (max 5000 characters).';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.status === 429) {
        toast.error('Too many messages. Please try again later.');
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Could not send your message. Please check your input and try again.');
        return;
      }
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '', company: '' });
      toast.success('Thanks — your message has been received.');
    } catch (_) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="contact-page">
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700 safe-top">
        <div className="max-w-3xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-slate-400 hover:text-white" data-testid="back-to-app-button">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to App
          </Button>
          <div className="flex-1" />
          <Link to="/legal" className="text-xs sm:text-sm text-slate-400 hover:text-amber-300 transition-colors">Legal Center</Link>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-amber-500/15 rounded-lg"><Mail className="h-5 w-5 sm:h-6 sm:w-6 text-amber-400" /></div>
          <h1 className="text-2xl sm:text-4xl font-bold text-white">Contact Us</h1>
        </div>
        <p className="text-slate-400 mb-6 leading-relaxed">
          Have a question, billing issue, privacy request, or security concern? Send us a message using the form below.
          Please do not include passwords, payment card details or brokerage credentials.
        </p>

        <div className="mb-8 rounded-xl border border-slate-700 bg-slate-800/40 p-4 text-sm" data-testid="contact-address">
          <p className="font-semibold text-white mb-1">Mailing address</p>
          <address className="not-italic leading-relaxed text-slate-400">
            Timothy Woods<br />Option Wizard<br />200 N. Vineyard Blvd<br />Ste A325-186<br />Honolulu, Hawaii 96817
          </address>
        </div>

        {sent ? (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center" data-testid="contact-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-white">Message received</h2>
            <p className="text-sm text-emerald-200/80 mt-1">Thanks for reaching out. We&rsquo;ve recorded your message.</p>
            <Button className="mt-5" variant="outline" onClick={() => setSent(false)} data-testid="send-another-btn">Send another message</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form" noValidate>
            {/* Honeypot (hidden from users) */}
            <div className="hidden" aria-hidden="true">
              <label>Company<input tabIndex={-1} autoComplete="off" value={form.company} onChange={set('company')} /></label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="c-name" className="text-slate-300">Name</Label>
                <Input id="c-name" value={form.name} onChange={set('name')} className="mt-1.5 bg-slate-800 border-slate-700 text-white" placeholder="Your name" data-testid="contact-name" />
                {errors.name && <p className="text-xs text-red-400 mt-1" data-testid="error-name">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="c-email" className="text-slate-300">Email</Label>
                <Input id="c-email" type="email" value={form.email} onChange={set('email')} className="mt-1.5 bg-slate-800 border-slate-700 text-white" placeholder="you@example.com" data-testid="contact-email" />
                {errors.email && <p className="text-xs text-red-400 mt-1" data-testid="error-email">{errors.email}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="c-subject" className="text-slate-300">Subject</Label>
              <Input id="c-subject" value={form.subject} onChange={set('subject')} className="mt-1.5 bg-slate-800 border-slate-700 text-white" placeholder="How can we help?" data-testid="contact-subject" />
              {errors.subject && <p className="text-xs text-red-400 mt-1" data-testid="error-subject">{errors.subject}</p>}
            </div>

            <div>
              <Label htmlFor="c-message" className="text-slate-300">Message</Label>
              <textarea
                id="c-message"
                value={form.message}
                onChange={set('message')}
                rows={6}
                maxLength={5000}
                className="mt-1.5 w-full rounded-md bg-slate-800 border border-slate-700 text-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-y"
                placeholder="Write your message (at least 10 characters)…"
                data-testid="contact-message"
              />
              {errors.message && <p className="text-xs text-red-400 mt-1" data-testid="error-message">{errors.message}</p>}
            </div>

            <Button type="submit" disabled={submitting} className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold" data-testid="contact-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              {submitting ? 'Sending…' : 'Send Message'}
            </Button>

            <p className="text-xs text-slate-500">
              By submitting, you agree to our{' '}
              <Link to="/privacy" className="text-amber-300 hover:underline underline-offset-4">Privacy Policy</Link>. We use
              your message only to respond to your inquiry.
            </p>
          </form>
        )}
      </main>

      <SiteFooter />
    </div>
  );
};

export default Contact;
